import { NavLink, useLocation } from 'react-router-dom'
import { Menu, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NAV_ITEMS, TAB_ROUTES, isUnder } from './navItems'

const TAB =
  'flex min-w-0 flex-1 flex-col items-center gap-0.5 pt-1.5 text-[10.5px] font-medium text-[var(--fl-text-2)]'
const TAB_ON = 'font-bold text-[var(--fl-primary)]'
// The selected tab's icon sits in a soft mint pill.
const PILL = 'flex h-7 w-14 items-center justify-center rounded-full transition-colors'
const PILL_ON = 'bg-[var(--fl-primary-subtle)]'

/** Phone and tablet navigation: three tabs, a raised ＋ in the middle, and Menu for the rest. */
export function MobileTabBar({
  onAdd,
  onMenu,
  menuOpen,
  menuDot = false,
}: {
  /** Absent when the household is read-only — there's nothing to add. */
  onAdd?: () => void
  onMenu: () => void
  menuOpen: boolean
  /** Something inside Menu needs attention — reminders due. */
  menuDot?: boolean
}) {
  const { pathname } = useLocation()
  const tabs = TAB_ROUTES.map((to) => NAV_ITEMS.find((item) => item.to === to)!)
  const inMenu = !TAB_ROUTES.some((route) => isUnder(pathname, route))

  const tab = (item: (typeof tabs)[number]) => (
    <NavLink key={item.to} to={item.to} className={({ isActive }) => cn(TAB, isActive && TAB_ON)}>
      {({ isActive }) => (
        <>
          <span className={cn(PILL, isActive && PILL_ON)}>
            <item.Icon size={19} aria-hidden="true" />
          </span>
          {item.label}
        </>
      )}
    </NavLink>
  )

  return (
    <nav
      aria-label="Tabs"
      className="fixed inset-x-0 bottom-0 z-30 flex h-[calc(64px+env(safe-area-inset-bottom))] items-start border-t border-[var(--fl-border)] bg-[var(--fl-surface)] px-1 pb-[env(safe-area-inset-bottom)] lg:hidden [body[data-hide-tab-bar]_&]:hidden"
    >
      {tab(tabs[0])}
      {tab(tabs[1])}
      {onAdd && (
        <div className="flex flex-1 justify-center">
          <button
            type="button"
            aria-label="Add transaction"
            onClick={onAdd}
            className="-mt-5 grid size-14 place-items-center rounded-full bg-[var(--fl-primary)] text-[var(--fl-primary-fg)] shadow-[0_8px_20px_rgba(45,122,95,0.35)] active:scale-95"
          >
            <Plus size={26} aria-hidden="true" />
          </button>
        </div>
      )}
      {tab(tabs[2])}
      <button
        type="button"
        onClick={onMenu}
        aria-expanded={menuOpen}
        aria-current={inMenu ? 'page' : undefined}
        className={cn(TAB, inMenu && TAB_ON)}
      >
        <span className={cn(PILL, 'relative', inMenu && PILL_ON)}>
          <Menu size={19} aria-hidden="true" />
          {menuDot && (
            <span
              aria-label="Reminders due"
              className="absolute right-3 top-0.5 h-2 w-2 rounded-full bg-[var(--fl-warning-text)] ring-2 ring-[var(--fl-surface)]"
            />
          )}
        </span>
        Menu
      </button>
    </nav>
  )
}
